import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ElementRef,
  OnInit,
  SimpleChanges,
  OnChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { IngredientChoice, OrderableIngredient, OrderedIngredient, OrderableQualifier, StoreConfig, AuditService } from 'ngx-web-api';
import { IngredientSelection } from '../../../../../domain/ingredient-selection';
import { Animations } from '../../../../../animations';
import { ExtraIngredientUpsellService } from 'app/core/services/extra-ingredient-upsell.service';
import { filter, take } from 'rxjs/operators';
import { ExtraUpsell } from 'app/domain/extra-upsell';
import { noop, of, Observable } from 'rxjs';
import { faCaretDown, faCaretUp } from '@fortawesome/pro-solid-svg-icons';
import { FocusService } from 'app/focus.service';
import { ConfirmationModalContext } from 'app/domain/confirmation-modal-context';
import { ModalService } from 'app/core/services/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { BreakpointService } from 'app/core/services/breakpoint.service';
import { HalfsOrderingService } from 'app/core/services/halfs-ordering.service';
import { HalfSelectionSideType } from 'ngx-web-api/lib/models/ordering/half-selection-side.enum';

@Component({
  selector: 'fts-ingredient-flat',
  templateUrl: './ingredient-flat.component.html',
  styleUrls: ['./ingredient-flat.component.scss'],
  animations: [Animations.scaleX],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IngredientFlatComponent implements OnInit, OnChanges {
  @Input()
  ingredient: OrderableIngredient;
  @Input()
  orderedIngredient: OrderedIngredient;
  @Input()
  itemSize: string;
  @Input()
  singleSelectionChoiceSelectedIngredient: OrderedIngredient;
  @Input()
  choice: IngredientChoice;
  @Input()
  disabled: boolean;
  @Input()
  isInModal: boolean;
  @Input()
  ingredientIndex: number;
  @Input()
  needsMaxIngredientsWarningsCheck: boolean;
  @Input()
  storeConfig: StoreConfig;
  @Input()
  inItemEditorModal: boolean;
  @Input()
  hasTags: boolean;
  @Input()
  isHalfAndHalf = false;

  @Output()
  ingredientSelectedChange: EventEmitter<OrderableIngredient> = new EventEmitter();
  @Output()
  ingredientHalfChange: EventEmitter<IngredientSelection> = new EventEmitter();
  @Output()
  ingredientQualifierChange: EventEmitter<IngredientSelection> = new EventEmitter();
  @Output()
  tagClicked: EventEmitter<string> = new EventEmitter<string>();

  faCaretDown = faCaretDown;
  faCaretUp = faCaretUp;
  singleDefaultName: string;
  isDisabled: boolean;
  isDisabledQualifier: boolean;
  isUnavailable: boolean;
  faTimes = faTimes;

  themableCheckBoxInitialValue: boolean;

  @ViewChild('popoverRef', { static: false })
  public popoverRef: ElementRef;

  @ViewChild('ingredientWrap', { static: false })
  ingredientWrapRef: ElementRef;

  public extraUpsell: ExtraUpsell;
  showSecondHalves = false;
  calories: number;
  servings: string;
  displayedIngredientPrice: number;
  availableSizes: string;

  qualifiers = [];

  constructor(
    private extraUpsellService: ExtraIngredientUpsellService,
    private elRef: ElementRef,
    private focusService: FocusService,
    private modalService: ModalService,
    private translate: TranslateService,
    private cdRef: ChangeDetectorRef,
    private auditService: AuditService,
    public breakPointService: BreakpointService,
    private halfsOrderingService: HalfsOrderingService
  ) {
    this.extraUpsellService.extraUpsell$
      .pipe(
        filter(upsell => upsell.choice === this.choice.name && upsell.ingredient === this.ingredient.name),
        take(1)
      )
      .subscribe(upsell => {
        this.extraUpsell = upsell;
        this.cdRef.detectChanges();
        setTimeout(() => {
          if (this.popoverRef && !this.extraUpsellService.isPopupOpen()) {
            this.extraUpsellService.setPopupOpen(true);
            this.focusService.activateTrapAndScrollToElement(this.popoverRef, 48); // 48px is the height of the fixed item editor header
          }
        }, 50);
      }, noop);

    this.extraUpsellService.documentClickEvent$
      .pipe(filter(event => !this.elRef.nativeElement.contains(event.target)))
      .subscribe(() => this.denyExtraUpsell(), noop);
  }

  ngOnInit() {
    this.singleDefaultName =
      this.choice &&
      this.choice.needsSingleIngredientSelection &&
      (this.choice.ingredients.find(i => i.acknowledgeRemovedDefaults) || { name: null }).name;
    this.calcShowSecondHalves();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['disabled'] || changes['itemSize'] || changes['choice']) {
      if (!!this.itemSize) {
        this.calcUnavailable();
      }
      if (!!this.choice) {
        this.calcDisabled();
      }
    }

    if (changes['orderedIngredient']) {
      if (this.isHalfAndHalf && this.ingredient.allowHalves) {
        this.themableCheckBoxInitialValue = this.halfsOrderingService.getValueForThemableOption(this.ingredient?.name);
      } else {
        this.themableCheckBoxInitialValue = !!this.orderedIngredient;
      }

      this.recalculateQualifiers();
      this.cdRef.detectChanges();
      this.calcShowSecondHalves();
    }
  }

  calcShowSecondHalves() {
    this.showSecondHalves =
      this.ingredient?.allowSecondHalves && (this.orderedIngredient?.isLeftHalf || this.orderedIngredient?.isRightHalf);
    this.cdRef.detectChanges();
  }

  calcDisabled() {
    this.isDisabledQualifier = this.disabled || this.isUnavailable;
    this.isDisabled = (this.choice.defaultsNonRemovable && this.ingredient.isDefault) || this.disabled || this.isUnavailable;
  }

  calcUnavailable() {
    this.isUnavailable = !this.ingredient.getSizePrice(this.itemSize);
  }

  toggleIngredient(ingredient) {
    if ((this.ingredient.acknowledgeRemovedDefaults && !!this.orderedIngredient) || ingredient === null) {
      this.acknowledgeModal$().subscribe(result => {
        if (!result) {
          return;
        }
        if (this.extraUpsell) {
          this.extraUpsell = null;
          this.extraUpsellService.setPopupOpen(false);
          this.extraUpsellService.denyExtraUpsell();
        }
        this.ingredientSelectedChange.emit(ingredient);
      });
      return;
    }

    if (this.extraUpsell) {
      this.extraUpsell = null;
      this.extraUpsellService.setPopupOpen(false);
      this.extraUpsellService.denyExtraUpsell();
    }
    this.ingredientSelectedChange.emit(ingredient);
    this.recalculateQualifiers();
    this.cdRef.detectChanges();
  }

  selectHalf(isLeftHalf: boolean, isRightHalf: boolean, isSecondHalf: boolean = false) {
    if (this.choice.defaultsNonRemovable && this.ingredient.isDefault) {
      return;
    }
    if (!isLeftHalf && !isRightHalf) {
      if (isSecondHalf) {
        document.getElementById(`first-half-whole-${this.orderedIngredient.ingredient}`)?.focus();
      }
      if (this.orderedIngredient?.secondHalfQualifiers?.length) {
        delete this.orderedIngredient['secondHalfQualifiers'];
        this.orderedIngredient.isSecondHalf = false;
      }
    }
    if (
      this.ingredient.acknowledgeRemovedDefaults &&
      !!this.orderedIngredient &&
      (isLeftHalf || isRightHalf) &&
      !this.orderedIngredient.isRightHalf &&
      !this.orderedIngredient.isLeftHalf
    ) {
      this.acknowledgeModal$(true).subscribe(result => {
        if (!result) {
          return;
        }
        this.ingredientHalfChange.emit({
          ingredient: this.ingredient,
          isLeftHalf: isLeftHalf,
          isRightHalf: isRightHalf,
          isSecondHalf: isSecondHalf,
        });
      });
      return;
    }
    this.ingredientHalfChange.emit({
      ingredient: this.ingredient,
      isLeftHalf: isLeftHalf,
      isRightHalf: isRightHalf,
      isSecondHalf: isSecondHalf,
    });
  }

  onIngredientClick() {
    if (this.isUnavailable && this.storeConfig.showSizeSpecificIngredients) {
      let availableSizes = '';
      availableSizes = this.ingredient.sizePrices.map(sp => sp.size).join(', ');
      const modalTitle = this.translate.instant('component.item_editor.choice.ingredient.unavailable_size_ingredients.modal_title');
      const modalBody = this.translate.instant('component.item_editor.choice.ingredient.unavailable_size_ingredients.modal_body', {
        ingredientName: this.ingredient.name,
        size: this.itemSize,
        availableSizes: availableSizes,
      });
      this.auditService.createAudit(() => `${modalTitle} modal opened`);
      this.modalService.openWarningNotificationModal(modalTitle, modalBody, true).subscribe(result => {
        if (result === null) {
          this.auditService.createAudit(() => `"OK" button has been clicked`);
        }
      });
    }
  }

  toggleQualifier(qualifier: OrderableQualifier, extra = false) {
    this.ingredientQualifierChange.emit({
      ingredient: this.ingredient,
      qualifier: qualifier,
      isExtraUpsell: extra,
    });
    if (extra) {
      this.extraUpsell = null;
      this.extraUpsellService.setPopupOpen(false);
    }
    this.recalculateQualifiers();
  }

  toggleSecondQualifier(qualifier: OrderableQualifier) {
    this.ingredientQualifierChange.emit({
      ingredient: this.ingredient,
      qualifier: qualifier,
      isSecondHalf: true,
    });
    this.recalculateQualifiers();
  }

  denyExtraUpsell() {
    this.extraUpsell = null;
    this.extraUpsellService.denyExtraUpsell();
  }

  onCaloriesCalc(value) {
    this.calories = value;
  }

  onServingsCalc(value) {
    this.servings = value;
  }

  onDisplayedIngredientPriceCalc(value) {
    this.displayedIngredientPrice = value;
  }

  onAvailableSizesCalc(value) {
    this.availableSizes = value;
  }

  private acknowledgeModal$(half = false): Observable<any> {
    const context = new ConfirmationModalContext();
    context.textOnly = false;
    context.reverseButtons = true;
    if (half) {
      context.mainContent = `
      <h3 class="text-uppercase">${this.translate.instant('component.item_editor.choice.ingredient.remove_default.sure')}</h3>
      <div class="font-size-larger">
        <div>${this.translate.instant('component.item_editor.choice.ingredient.remove_default.did_half')}</div>
        <div class="text-uppercase text-danger fw-600">
          ${this.translate.instant('component.item_editor.choice.ingredient.remove_default.on_half', {
            name: this.singleDefaultName || this.ingredient.name,
          })}
        </div>
        <div>${this.translate.instant('component.item_editor.choice.ingredient.remove_default.of')}</div>
      </div>
    `;
    } else {
      context.mainContent = `
      <h3 class="text-uppercase">${this.translate.instant('component.item_editor.choice.ingredient.remove_default.sure')}</h3>
      <div class="font-size-larger">
        <div>${this.translate.instant('component.item_editor.choice.ingredient.remove_default.did_whole')}</div>
        <div class="text-uppercase text-danger fw-600">
          ${this.translate.instant('component.item_editor.choice.ingredient.remove_default.remove', {
            name: this.singleDefaultName || this.ingredient.name,
          })}
        </div>
        <div>${this.translate.instant('component.item_editor.choice.ingredient.remove_default.from')}</div>
      </div>
    `;
    }
    context.submitBtnTitle = this.translate.instant('component.item_editor.choice.ingredient.remove_default.confirm');
    context.dismissBtnTitle = this.translate.instant('component.item_editor.choice.ingredient.remove_default.dismiss');
    context.submitObservable = of(true);
    return this.modalService.openConfirmationModal(context, null, true);
  }

  recalculateQualifiers() {
    if (!this.orderedIngredient) {
      return;
    }
    this.qualifiers = [];
    if (this.isHalfAndHalf && this.ingredient.allowHalves && (this.orderedIngredient.isLeftHalf || this.orderedIngredient.isRightHalf)) {
      const isLeft = this.halfsOrderingService.activeHalfSide.getValue() === HalfSelectionSideType.LEFT;
      if ((isLeft && this.orderedIngredient.isLeftHalf) || (!isLeft && this.orderedIngredient.isRightHalf)) {
        if (this.orderedIngredient.qualifiers?.length) {
          this.qualifiers = this.qualifiers.concat(this.orderedIngredient.qualifiers);
        }
      } else {
        if (this.orderedIngredient.secondHalfQualifiers?.length) {
          this.qualifiers = this.qualifiers.concat(this.orderedIngredient.secondHalfQualifiers);
        }
      }
    } else {
      if (this.orderedIngredient.qualifiers?.length) {
        this.qualifiers = this.qualifiers.concat(this.orderedIngredient.qualifiers);
      }
    }
  }
}
